import type { UnknownAction } from "@reduxjs/toolkit"
import * as Sentry from "@sentry/react"

import { sentryStateTransformer } from "./sentry-scrub"

/**
 * Sentry's Redux enhancer records every action as a breadcrumb, `normalizeDepth`
 * 10 deep (main.tsx). An RTK Query action carries its call's arguments in
 * `meta.arg.originalArgs` (a pasted Meta token, a login password), the cache
 * key built from them (a reset-password nonce) and the response body (a freshly
 * minted API key). The breadcrumb keeps only the action type, the endpoint, the
 * request status and the arguments, with the values of secret-named keys
 * filtered at any depth; the dispatched action is never changed.
 */
const SENSITIVE_KEY = /token|secret|password|passwd|api[-_]?key/i
const FILTERED = "[Filtered]"

/**
 * The RTK Query thunks and slices whose actions carry a cache key or a body:
 * `<reducerPath>/executeQuery/fulfilled`, `<reducerPath>/queries/…`.
 */
const RTK_QUERY_ACTION =
  /^[^/]+\/(?:executeQuery|executeMutation|queries|subscriptions|internalSubscriptions|invalidation)\//

function redact(value: unknown, depth = 0): unknown {
  if (depth > 10 || value === null || typeof value !== "object") return value
  if (Array.isArray(value)) return value.map(item => redact(item, depth + 1))
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      SENSITIVE_KEY.test(key) ? FILTERED : redact(item, depth + 1),
    ]),
  )
}

type RtkQueryThunkMeta = {
  requestStatus?: unknown
  arg?: { endpointName?: unknown; originalArgs?: unknown }
}

export function sentryActionTransformer(action: UnknownAction): UnknownAction {
  if (!RTK_QUERY_ACTION.test(action.type)) return action
  const { requestStatus, arg } = (action.meta ?? {}) as RtkQueryThunkMeta
  if (!arg) return { type: action.type }
  return {
    type: action.type,
    meta: {
      requestStatus,
      arg: {
        endpointName: arg.endpointName,
        originalArgs: redact(arg.originalArgs),
      },
    },
  }
}

/**
 * Both halves of the leak, in one place. `actionTransformer` scrubs the RTK
 * Query action breadcrumbs (#4958) and `stateTransformer` drops the RTK Query
 * cache slices from the state Sentry attaches to every error (#4962). The two
 * shipped as separate PRs, each leaving the other as a TODO; keep them
 * together, because dropping either one reopens that half silently.
 */
export const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  actionTransformer: sentryActionTransformer,
  stateTransformer: sentryStateTransformer,
})
